/**
 * A tenant's OMR sheet designs, to look at: every version, and the chosen one
 * drawn — each printed row's box over the blank sheet, or over an empty page of
 * the same shape when no blank is stored. Read-only: designs are saved,
 * activated and given their blank by scripts/ops/omrSheetDesign.ts; the
 * default is chosen in the Settings tab (OmrDefaultSetting). See
 * docs/FeatureDesigns/assignments-tests/omr-reader-target.md §2.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { EXAM_VARIANT_LABELS } from '@padcanvas/shared';
import { Card, CardHeader, CardContent, Badge } from '@shared-ui/components';
import { tenantsApi, type TenantOmrSheet } from '../../../api/tenantsApi';

const KIND_COLOR: Record<TenantOmrSheet['rows'][number]['kind'], string> = {
    choice: '#2563eb',
    integer: '#7c3aed',
};

const STATUS_VARIANT = { active: 'success', draft: 'warning', retired: 'default' } as const;

const key = (d: TenantOmrSheet) => `${d.sheetId}.v${d.version}`;

function SheetDrawing({ design }: { design: TenantOmrSheet }) {
    const [focus, setFocus] = useState<number | null>(null);
    const [w, h] = design.page;
    const pct = (n: number, of: number) => `${(n / of) * 100}%`;
    const focused = design.rows.find(r => r.row === focus);

    return (
        <div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)', minHeight: 20, marginBottom: 4 }}>
                {focused
                    ? `Row ${focused.row} · ${focused.kind === 'integer' ? `integer, ${focused.digits} digit${focused.digits === 1 ? '' : 's'}` : 'choice'}`
                    : 'Point at a box to see its row.'}
            </div>
            <div
                style={{
                    position: 'relative', maxWidth: 560, aspectRatio: `${w} / ${h}`,
                    border: '1px solid var(--border-color)',
                    background: design.blankSheetUrl ? undefined : 'var(--bg-secondary)',
                }}
            >
                {design.blankSheetUrl && (
                    <img src={design.blankSheetUrl} alt={`${design.name}, blank`} style={{ width: '100%', height: '100%', display: 'block' }} />
                )}
                {design.rows.map(r => {
                    const [x, y, bw, bh] = r.box;
                    const on = focus === r.row;
                    return (
                        <div
                            key={r.row}
                            title={`Row ${r.row}`}
                            onMouseEnter={() => setFocus(r.row)}
                            onMouseLeave={() => setFocus(null)}
                            style={{
                                position: 'absolute', left: pct(x, w), top: pct(y, h), width: pct(bw, w), height: pct(bh, h),
                                border: `${on ? 2.5 : 1}px solid ${KIND_COLOR[r.kind]}`,
                                background: on ? `${KIND_COLOR[r.kind]}33` : 'transparent',
                                boxSizing: 'border-box',
                            }}
                        />
                    );
                })}
            </div>
            <p className="muted" style={{ fontSize: 13 }}>
                Boxes: blue a choice row, purple an integer row.{' '}
                {design.blankSheetUrl
                    ? 'The sheet is placed on its corner markers, as a student’s page is before it is read.'
                    : <>No blank sheet is stored for this version — make one with <code>Backend/omr-reader/tools/place_blank.py</code> and store it with <code>omrSheetDesign.ts blank</code>.</>}
            </p>
        </div>
    );
}

export function OmrDesignsCard({ tenantId }: { tenantId: string }) {
    const { data, isLoading, error } = useQuery({
        queryKey: ['tenant-omr-sheets', tenantId],
        queryFn: () => tenantsApi.omrSheets(tenantId),
    });
    const [chosen, setChosen] = useState<string | null>(null);
    const designs = data?.designs ?? [];

    useEffect(() => {
        if (!chosen && designs.length) setChosen(key(designs.find(d => d.status === 'active') ?? designs[0]));
    }, [designs, chosen]);

    const design = designs.find(d => key(d) === chosen);

    return (
        <Card padding="none">
            <CardHeader
                title="OMR sheet designs"
                subtitle="The sheet layouts this tenant's OMR pages are read against. Saved and activated with scripts/ops/omrSheetDesign.ts."
            />
            <CardContent>
                {isLoading && <p>Loading…</p>}
                {error && <div className="tenants-error">{(error as Error).message}</div>}
                {data && !designs.length && <p className="muted">No designs. Its OMR pages cannot be read until one is saved and activated.</p>}
                {designs.length > 0 && (
                    <table className="data-table" style={{ fontSize: 13, marginBottom: 16 }}>
                        <thead>
                            <tr><th>Design</th><th>Version</th><th>Status</th><th>Exam</th><th>Rows</th><th>Verified</th><th>Blank</th></tr>
                        </thead>
                        <tbody>
                            {designs.map(d => {
                                const v = d.verification;
                                return (
                                    <tr
                                        key={key(d)}
                                        onClick={() => setChosen(key(d))}
                                        style={{ cursor: 'pointer', background: key(d) === chosen ? 'var(--bg-secondary)' : undefined }}
                                    >
                                        <td>
                                            {d.name}
                                            <div className="muted"><code>{d.sheetId}</code>{data?.defaultSheetId === d.sheetId && ' · tenant default'}</div>
                                        </td>
                                        <td>v{d.version}</td>
                                        <td><Badge variant={STATUS_VARIANT[d.status]} size="sm">{d.status}</Badge></td>
                                        <td>{d.exam ? EXAM_VARIANT_LABELS[d.exam] : '—'}</td>
                                        <td>{d.rows.length}</td>
                                        <td title={v?.samples.map(s => `${s.name}: ${s.fieldsRight}/${s.fieldsTotal} fields, ${s.cropsRefused}/${s.crops} crops refused`).join('\n')}>
                                            {v ? `${v.passed ? 'passed' : 'failed'} on ${v.samples.length} sheet${v.samples.length === 1 ? '' : 's'}` : '—'}
                                        </td>
                                        <td>{d.blankSheetUrl ? 'stored' : '—'}</td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                )}
                {design && <SheetDrawing key={key(design)} design={design} />}
            </CardContent>
        </Card>
    );
}
